// =====================================================================
// COMPONENT: region-market-select.component.ts
// FUNCTIONAL OVERVIEW:
//   Region + market pill selector. Editable mode drives the chat
//   region/market (ChatRegionMarketService). Read-only mode only displays
//   the user's platform region/market (PlatformRegionService).
// DEPENDENCIES: ChatRegionMarketService, PlatformRegionService, SelectComponent
// =====================================================================
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ChatRegionMarketService } from '../../../modules/roleplay/services/chat-region-market.service';
import { Market } from '../../../../model/shared/platform-region.model';
import { PlatformRegionService } from '../../../../service/shared/platform-region.service';
import { AppFormControlField } from '../../models/form-controls.model';
import { SelectComponent } from '../form-controls/select/select.component';

type RegionMarketSource = Pick<
  ChatRegionMarketService,
  'regionsList' | 'marketList' | 'selected_region_code' | 'selected_region_label' | 'selected_market_code' | 'marketSubject$'
>;

@Component({
    selector: 'app-region-market-select',
    templateUrl: './region-market-select.component.html',
    styleUrl: './region-market-select.component.scss',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, SelectComponent]
})
export class RegionMarketSelectComponent implements OnInit, OnDestroy {

  @ViewChild('searchRegion') regionSearchTextBox: ElementRef;
  @ViewChild('searchMarket') marketSearchTextBox: ElementRef;

  /** Display the platform region/market without allowing changes. */
  @Input() readonly = false;

  @Output() selectionChanged = new EventEmitter<{ region: string; market: string }>();

  regionSelectFormControl = new FormControl();
  regionSearchTextboxControl = new FormControl();
  regionSelectedValues = '';
  regionData: string[] = [];
  regionFilteredOptions: Observable<string[]>;
  isRegionListDisabled = true;

  regionField: AppFormControlField = {
    label: 'Region',
    placeholder: 'Select',
    options: []
  };

  marketSelectFormControl = new FormControl();
  marketSearchTextboxControl = new FormControl();
  marketSelectedValues: string[] = [];
  marketFilteredOptions: Observable<Market[]>;
  marketField: AppFormControlField = {
    label: 'Market',
    placeholder: 'Select',
    multiple: true,
    options: []
  };

  private marketSubscription: Subscription;
  private regionControlSubscription: Subscription;
  private marketControlSubscription: Subscription;

  constructor(
    private chatRegionMarketService: ChatRegionMarketService,
    private platformRegionService: PlatformRegionService
  ) { }

  private get source(): RegionMarketSource {
    return this.readonly ? this.platformRegionService : this.chatRegionMarketService;
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Loads regions/markets and, in editable mode only, wires the
  //   region/market controls to update the chat selection.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5913] 2026-10-21 16:20 | Vikas Kumar | Added read-only mode using the platform selection.
  // -----------------------------------------------------------------
  ngOnInit(): void {
    this.marketSubscription = this.source.marketSubject$.subscribe(() => {
      this.loadRegions();
    });

    if (this.readonly) {
      this.loadRegions();
      return;
    }

    this.regionControlSubscription = this.regionSelectFormControl.valueChanges.subscribe((region: string) => {
      if (!region || region === this.chatRegionMarketService.selected_region_label) {
        return;
      }

      this.chatRegionMarketService.setDataForSelectedRegion(region);
      this.regionSelectedValues = region;
      this.resetMarketData();
    });

    this.marketControlSubscription = this.marketSelectFormControl.valueChanges.subscribe((values: string[] | string) => {
      const selectedValues = Array.isArray(values) ? values : (values ? [values] : []);
      this.marketSelectedValues = selectedValues;
      this.chatRegionMarketService.setSelectedMarketCode(this.marketSelectedValues.join(','));
      this.emitSelection();
    });

    this.loadRegions();
  }

  ngOnDestroy(): void {
    this.marketSubscription?.unsubscribe();
    this.regionControlSubscription?.unsubscribe();
    this.marketControlSubscription?.unsubscribe();
  }
  get selectedMarketsControl(): FormControl {
    return this.marketSelectFormControl as FormControl;
  }

  isSelected(code: string): boolean {
    const value = this.regionSelectFormControl?.value;
    return Array.isArray(value) && value.indexOf(code) > -1;
  }

  toggleSelection(code: string): void {
    const current: string[] = Array.isArray(this.selectedMarketsControl?.value)
      ? [...this.selectedMarketsControl.value]
      : [];

    const idx = current.indexOf(code);
    if (idx > -1) {
      current.splice(idx, 1);
    } else {
      current.push(code);
    }

    this.selectedMarketsControl.setValue(current);
    this.selectedMarketsControl.markAsDirty();
    this.selectedMarketsControl.markAsTouched();
    // If you need to trigger your existing selection-change handler, call it here:
    // this.marketSelectionChange(/* construct an event or call the handler logic directly */);
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Populates region options and the selected region, then markets.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5913] 2026-10-21 16:20 | Vikas Kumar | Read from active source; keep disabled in read-only mode.
  // -----------------------------------------------------------------
  private loadRegions(): void {
    const regions = this.source.regionsList ?? [];
    this.regionData = regions.map(r => r.label || r.name);
    this.regionField.options = regions.map(r => ({ label: r.label || r.name, value: r.label || r.name }));
    if (!this.regionData.length) return;

    this.isRegionListDisabled = this.readonly || this.regionData.length === 1;
    this.regionSelectFormControl.setValue(this.source.selected_region_label, { emitEvent: false });
    this.regionSelectedValues = this.source.selected_region_label || '';

    if (this.isRegionListDisabled) {
      this.regionSelectFormControl.disable();
    } else {
      this.regionSelectFormControl.enable();
    }

    this.regionFilteredOptions = this.regionSearchTextboxControl.valueChanges.pipe(
      startWith(''),
      map(name => this._filterRegion(name))
    );

    this.marketFilteredOptions = this.marketSearchTextboxControl.valueChanges.pipe(
      startWith(''),
      map(name => this._filterMarket(name))
    );

    // Load market if already available
    if (this.source.marketList?.length > 0) {
      this.loadMarket();
    }
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Populates market options and the selected market(s).
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5913] 2026-10-21 16:20 | Vikas Kumar | Read from active source; keep disabled in read-only mode.
  // -----------------------------------------------------------------
  private loadMarket(): void {
    const marketList = this.source.marketList;
    if (!marketList || marketList.length === 0) return;

    this.marketField.options = marketList.map((market: Market) => ({
      label: market.primary_market.name,
      value: market.primary_market.code
    }));

    const marketCode = this.source.selected_market_code;
    this.marketSelectedValues = marketCode ? marketCode.split(',') : [];
    this.marketSelectFormControl.setValue(this.marketSelectedValues, { emitEvent: false });

    if (this.readonly || marketList.length === 1) {
      this.marketSelectFormControl.disable();
    } else {
      this.marketSelectFormControl.enable();
    }

    // Re-initialize filtered options so mat-option elements render with the new market data
    this.marketFilteredOptions = this.marketSearchTextboxControl.valueChanges.pipe(
      startWith(''),
      map(name => this._filterMarket(name))
    );

    this.emitSelection();
  }

  private _filterRegion(name: string): string[] {
    const filterValue = (name || '').toLowerCase();
    this.regionSelectedValues = this.regionSelectFormControl.value || '';
    return this.regionData.filter(option => option.toLowerCase().indexOf(filterValue) === 0);
  }

  private _filterMarket(name: string): Market[] {
    const filterValue = (name || '').toLowerCase();
    this.marketSetSelectedValues();
    this.marketSelectFormControl.patchValue(this.marketSelectedValues);
    const marketList = this.chatRegionMarketService.marketList;
    if (!marketList) return [];
    return marketList.filter((option: Market) =>
      option.primary_market.name?.toLowerCase().indexOf(filterValue) === 0
    );
  }

  regionOpenedChange(e: boolean): void {
    this.regionSearchTextboxControl.patchValue('');
    if (e) {
      this.regionSearchTextBox?.nativeElement.focus();
    }
  }

  regionClearSearch(event: Event): void {
    event.stopPropagation();
    this.regionSearchTextboxControl.patchValue('');
  }

  marketOpenedChange(e: boolean): void {
    this.marketSearchTextboxControl.patchValue('');
    if (e) {
      this.marketSearchTextBox?.nativeElement.focus();
    }
  }

  marketClearSearch(event: Event): void {
    event.stopPropagation();
    this.marketSearchTextboxControl.patchValue('');
  }

  private marketSetSelectedValues(): void {
    const values = this.marketSelectFormControl.value;
    if (values && values.length > 0) {
      values.forEach((v: string) => {
        if (this.marketSelectedValues.indexOf(v) === -1) {
          this.marketSelectedValues.push(v);
        }
      });
      this.chatRegionMarketService.setSelectedMarketCode(this.marketSelectedValues.join(','));
    }
  }

  private resetMarketData(): void {
    this.marketSelectedValues = [];
    this.marketSelectFormControl.setValue([], { emitEvent: false });
    this.chatRegionMarketService.setSelectedMarketCode('');
    this.marketSelectFormControl.enable();
  }

  getMarketNameByCode(code: any): any {
    const marketList = this.chatRegionMarketService.marketList;
    if (!marketList) return null;

    if (Array.isArray(code)) {
      const names: string[] = [];
      code.forEach(cd => {
        marketList.forEach((market: Market) => {
          if (market.primary_market.code.toString() === cd.toString()) {
            names.push(market.primary_market.name);
          }
        });
      });
      return names;
    }

    if (typeof code === 'string') {
      const found = marketList.find((m: Market) => m.primary_market.code.toString() === code.toString());
      return found?.primary_market.name;
    }

    return null;
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Emits the current region code and comma-separated market codes.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5913] 2026-10-21 16:20 | Vikas Kumar | Emit from the active source.
  // -----------------------------------------------------------------
  private emitSelection(): void {
    this.selectionChanged.emit({
      region: this.source.selected_region_code || '',
      market: this.source.selected_market_code || ''
    });
  }
}